import { getUniqueJobs } from "../../utils/utils";
import { ActionType, getType } from "typesafe-actions";
import { IDeleteJobState, IJob, IJobListTimestampRange, IJobRequest, IJobs, JobStatus } from "../../models";
import * as actions from "../actions/jobAction";

export interface IJobState {
    jobId?: string,
    currentJobStatus: JobStatus,
    waitingForScan: boolean,
    jobRequest?: IJobRequest,
    draftJobId?: string,
    draftInfo: { [key: string]: string },
    draftLocked: boolean,
    committedDraftInfoByJobId: { [jobId: string]: { [key: string]: string } },
    createJob: ICreateJobState,
    queueJob: ICreateJobState,
    updateJob: IUpdateJobState,
    updateLocalJobInfo: IUpdateJobState,
    updateJobResult: IUpdateJobResultState,
    jobStatus: IJobStatusState,
    userJobs: IUserJobsState,
    userJobsRange: IJobListTimestampRange | undefined,
    orgJobs: IUserJobsState,
    orgJobsRange: IJobListTimestampRange | undefined,
    deleteJob: IDeleteJobState;
    // Averaging feature selection state
    selectedJobIds: string[];
    isSelectionMode: boolean;
}

export interface ICreateJobState {
    readonly error?: Error;
    readonly isFetching: boolean;
    readonly success: Boolean | null;
    readonly source?: string;
}

export interface IQueueJobState {
    readonly error?: Error;
    readonly isFetching: boolean;
    readonly success: Boolean | null;
}

export interface IUpdateJobState {
    readonly error?: Error;
    readonly isFetching: boolean;
    readonly success: Boolean | null;
}

export interface IUpdateJobResultState {
    readonly error?: Error;
    readonly isFetching: boolean;
    readonly success: Boolean | null;
}
export interface IJobStatusState {
    readonly error?: Error;
    readonly isFetching: boolean;
    readonly job?: IJob | null;
}

export interface IUserJobsState {
    readonly error?: Error;
    readonly isFetching: boolean;
    readonly jobs?: IJobs | null;
    readonly lastFetch: Date;
}

export const initialJobState: IJobState = {
    jobId: undefined,
    currentJobStatus: JobStatus.IDLE,
    waitingForScan: false,
    jobRequest: undefined,
    draftJobId: undefined,
    draftInfo: {},
    draftLocked: false,
    committedDraftInfoByJobId: {},
    createJob: {
        error: undefined,
        isFetching: false,
        success: false,
        source: undefined
    },
    queueJob: {
        error: undefined,
        isFetching: false,
        success: false
    },
    updateJob: {
        error: undefined,
        isFetching: false,
        success: false
    },
    updateLocalJobInfo: {
        error: undefined,
        isFetching: false,
        success: false
    },
    jobStatus: {
        error: undefined,
        isFetching: false,
        job: undefined
    },
    userJobs: {
        error: undefined,
        isFetching: false,
        jobs: undefined,
        lastFetch: new Date()
    },
    userJobsRange: undefined,
    orgJobs: {
        error: undefined,
        isFetching: false,
        jobs: undefined,
        lastFetch: new Date()
    },
    orgJobsRange: undefined,
    updateJobResult: {
        error: undefined,
        isFetching: false,
        success: false
    },
    deleteJob: {
        error: undefined,
        isFetching: false,
        success: false
    },
    // Averaging feature initial state
    selectedJobIds: [],
    isSelectionMode: false
};

export const JobReducer = (
    state: IJobState = initialJobState,
    action: ActionType<typeof actions>
): IJobState => {
    switch (action.type) {
        case getType(actions.setJobId):
            return {
                ...state,
                jobId: action.payload
            };
        case getType(actions.setDraftJobId):
            return {
                ...state,
                draftJobId: action.payload
            };
        case getType(actions.newDraftReading):
            return {
                ...state,
                draftJobId: action.payload,
                draftInfo: {},
                draftLocked: false
            };
        case getType(actions.setDraftLocked):
            return {
                ...state,
                draftLocked: action.payload
            };
        case getType(actions.setDraftInfoField):
            return {
                ...state,
                draftInfo: {
                    ...state.draftInfo,
                    [action.payload.fieldName]: action.payload.value
                }
            };
        case getType(actions.clearDraftInfo):
            return {
                ...state,
                draftInfo: {}
            };
        case getType(actions.commitDraftInfoForJob):
            return {
                ...state,
                committedDraftInfoByJobId: {
                    ...state.committedDraftInfoByJobId,
                    [action.payload.jobId]: action.payload.info || {}
                }
            };
        case getType(actions.clearCommittedDraftInfo):
            {
                if (!state.committedDraftInfoByJobId[action.payload]) {
                    return state;
                }
                const nextCommitted = { ...state.committedDraftInfoByJobId };
                delete nextCommitted[action.payload];
                return {
                    ...state,
                    committedDraftInfoByJobId: nextCommitted
                };
            }
        case getType(actions.setJobRequest):
            return {
                ...state,
                jobRequest: action.payload
            };
        case getType(actions.updateRpiJobStatus):
            const currentJobId = state.jobId;
            if (currentJobId === action.payload.jobId) {
                return {
                    ...state,
                    currentJobStatus: action.payload.status
                };
            } else {
                return state
            };
        case getType(actions.upsertOptimisticUserJob):
            {
                const payloadJob = action.payload;
                const existingJobs = state.userJobs.jobs?.Items || [];
                const existing = existingJobs.find((job) => job?.jobId === payloadJob.jobId);
                const merged: IJob = existing
                    ? {
                        ...existing,
                        ...payloadJob,
                        // Keep the original createdAt stable across optimistic updates.
                        createdAt: existing.createdAt ?? payloadJob.createdAt
                    }
                    : payloadJob;
                const nextJobs = getUniqueJobs([...existingJobs, merged], (it: IJob) => it.jobId);
                return {
                    ...state,
                    userJobs: {
                        error: undefined,
                        isFetching: false,
                        jobs: {
                            Items: nextJobs
                        },
                        lastFetch: new Date()
                    }
                };
            }
        case getType(actions.setWaitingForScan):
            return {
                ...state,
                waitingForScan: action.payload
            }
        case getType(actions.jobStatus.request):
            return {
                ...state,
                currentJobStatus: JobStatus.IDLE,
                jobStatus: {
                    error: undefined,
                    isFetching: true,
                    job: undefined
                }
            };
        case getType(actions.jobStatus.success):
            var jobs = state.userJobs.jobs?.Items || []
            if (action.payload !== undefined && action.payload !== null) {
                jobs.push(action.payload)
            }

            var uniqueJobs = getUniqueJobs(jobs, (it: IJob) => it.jobId)

            return {
                ...state,
                currentJobStatus: action.payload?.jobStatus as JobStatus || JobStatus.IDLE,
                jobStatus: {
                    error: undefined,
                    isFetching: false,
                    job: action.payload
                },
                userJobs: {
                    error: undefined,
                    isFetching: false,
                    jobs: {
                        "Items": uniqueJobs
                    },
                    lastFetch: new Date()
                }
            };
        case getType(actions.jobStatus.failure):
            return {
                ...state,
                currentJobStatus: JobStatus.IDLE,
                jobStatus: {
                    error: action.payload,
                    isFetching: false,
                }
            };
        case getType(actions.processLocalJob.request):
            return {
                ...state,
                currentJobStatus: JobStatus.JOB_PROCESSING_LOCAL,
                jobStatus: {
                    error: undefined,
                    isFetching: true,
                    job: state.jobStatus.job
                }
            };
        case getType(actions.processLocalJob.success):
            var localJobs = state.userJobs.jobs?.Items || [];
            if (action.payload) {
                localJobs.push(action.payload);
            }

            var uniqueLocalJobs = getUniqueJobs(localJobs, (it: IJob) => it.jobId);

            return {
                ...state,
                currentJobStatus: action.payload?.jobStatus as JobStatus || JobStatus.IDLE,
                jobStatus: {
                    error: undefined,
                    isFetching: false,
                    job: action.payload
                },
                userJobs: {
                    error: undefined,
                    isFetching: false,
                    jobs: {
                        "Items": uniqueLocalJobs
                    },
                    lastFetch: new Date()
                }
            };
        case getType(actions.processLocalJob.failure):
            return {
                ...state,
                currentJobStatus: JobStatus.JOB_FAILED,
                jobStatus: {
                    error: action.payload,
                    isFetching: false,
                }
            };
        case getType(actions.processLocalAveragingJob.request):
            return {
                ...state,
                currentJobStatus: JobStatus.JOB_PROCESSING_LOCAL,
                createJob: {
                    error: undefined,
                    isFetching: true,
                    success: false,
                    source: 'averaging'
                }
            };
        case getType(actions.processLocalAveragingJob.success):
            var localAveragingJobs = state.userJobs.jobs?.Items || [];
            if (action.payload) {
                localAveragingJobs.push(action.payload);
            }

            var uniqueLocalAveragingJobs = getUniqueJobs(localAveragingJobs, (it: IJob) => it.jobId);

            return {
                ...state,
                currentJobStatus: action.payload?.jobStatus as JobStatus || JobStatus.IDLE,
                createJob: {
                    error: undefined,
                    isFetching: false,
                    success: true,
                    source: 'averaging'
                },
                jobStatus: {
                    error: undefined,
                    isFetching: false,
                    job: action.payload
                },
                userJobs: {
                    error: undefined,
                    isFetching: false,
                    jobs: {
                        "Items": uniqueLocalAveragingJobs
                    },
                    lastFetch: new Date()
                }
            };
        case getType(actions.processLocalAveragingJob.failure):
            return {
                ...state,
                currentJobStatus: JobStatus.JOB_FAILED,
                createJob: {
                    error: action.payload,
                    isFetching: false,
                    success: false,
                    source: 'averaging'
                }
            };
        case getType(actions.createJob.request):
            return {
                ...state,
                createJob: {
                    error: undefined,
                    isFetching: true,
                    success: false,
                    source: action.payload.source
                }
            };
        case getType(actions.createJob.success):
            return {
                ...state,
                createJob: {
                    error: undefined,
                    isFetching: false,
                    success: action.payload,
                    source: state.createJob.source
                }
            };
        case getType(actions.createJob.failure):
            return {
                ...state,
                createJob: {
                    ...state.createJob,
                    error: action.payload,
                    isFetching: false,
                }
            };
        case getType(actions.queueJob.request):
            return {
                ...state,
                queueJob: {
                    error: undefined,
                    isFetching: true,
                    success: false
                }
            };
        case getType(actions.queueJob.success):
            return {
                ...state,
                queueJob: {
                    error: undefined,
                    isFetching: false,
                    success: action.payload
                }
            };
        case getType(actions.queueJob.failure):
            return {
                ...state,
                queueJob: {
                    ...state.createJob,
                    error: action.payload,
                    isFetching: false,
                }
            };
        case getType(actions.updateJob.request):
            return {
                ...state,
                updateJob: {
                    error: undefined,
                    isFetching: true,
                    success: false
                }
            };
        case getType(actions.updateJob.success):
            return {
                ...state,
                updateJob: {
                    error: undefined,
                    isFetching: false,
                    success: action.payload
                }
            };
        case getType(actions.updateJob.failure):
            return {
                ...state,
                updateJob: {
                    ...state.updateJob,
                    error: action.payload,
                    isFetching: false,
                }
            };
        case getType(actions.updateLocalJobInfo.request):
            return {
                ...state,
                updateLocalJobInfo: {
                    error: undefined,
                    isFetching: true,
                    success: false
                }
            };
        case getType(actions.updateLocalJobInfo.success):
            return {
                ...state,
                updateLocalJobInfo: {
                    error: undefined,
                    isFetching: false,
                    success: action.payload
                }
            };
        case getType(actions.updateLocalJobInfo.failure):
            return {
                ...state,
                updateLocalJobInfo: {
                    ...state.updateLocalJobInfo,
                    error: action.payload,
                    isFetching: false
                }
            };
        case getType(actions.updateJobResult.request):
            return {
                ...state,
                updateJobResult: {
                    error: undefined,
                    isFetching: true,
                    success: false
                }
            };
        case getType(actions.updateJobResult.success):
            return {
                ...state,
                updateJobResult: {
                    error: undefined,
                    isFetching: false,
                    success: action.payload
                }
            };
        case getType(actions.updateJobResult.failure):
            return {
                ...state,
                updateJobResult: {
                    ...state.updateJobResult,
                    error: action.payload,
                    isFetching: false,
                }
            };
        case getType(actions.pollJobStatus.request):
            return {
                ...state,
                currentJobStatus: JobStatus.IDLE,
                jobStatus: {
                    error: undefined,
                    isFetching: true,
                    job: undefined
                }
            };
        case getType(actions.pollJobStatus.success):
            var jobs = state.userJobs.jobs?.Items || []
            if (action.payload !== undefined && action.payload !== null) {
                jobs.push(action.payload)
            }

            var uniqueJobs = getUniqueJobs(jobs, (it: IJob) => it.jobId)

            return {
                ...state,
                currentJobStatus: action.payload?.jobStatus as JobStatus || JobStatus.IDLE,
                jobStatus: {
                    error: undefined,
                    isFetching: false,
                    job: action.payload
                },
                userJobs: {
                    error: undefined,
                    isFetching: false,
                    jobs: {
                        "Items": uniqueJobs
                    },
                    lastFetch: new Date()
                }
            };
        case getType(actions.pollJobStatus.failure):
            return {
                ...state,
                currentJobStatus: JobStatus.IDLE,
                jobStatus: {
                    error: action.payload,
                    isFetching: false,
                }
            };
        case getType(actions.userJobs.request):
            return {
                ...state,
                userJobs: {
                    ...state.userJobs,
                    error: undefined,
                    isFetching: true
                }
            };
        case getType(actions.userJobs.success):
            var jobs = state.userJobs.jobs?.Items || []
            if (action.payload?.Items !== undefined) {
                jobs.push(...action.payload?.Items)
            }

            var uniqueJobs = getUniqueJobs(jobs, (it: IJob) => it.jobId)

            return {
                ...state,
                userJobs: {
                    error: undefined,
                    isFetching: false,
                    jobs: {
                        "Items": uniqueJobs
                    },
                    lastFetch: new Date()
                }
            };
        case getType(actions.userJobs.failure):
            return {
                ...state,
                userJobs: {
                    ...state.userJobs,
                    error: action.payload,
                    isFetching: false,
                }
            };
        case getType(actions.pollUserJobs.request):
            return {
                ...state,
                userJobs: {
                    ...state.userJobs,
                    error: undefined,
                    isFetching: true
                }
            };
        case getType(actions.pollUserJobs.success):
            var jobs = state.userJobs.jobs?.Items || []
            if (action.payload?.Items !== undefined) {
                jobs.push(...action.payload?.Items)
            }

            var uniqueJobs = getUniqueJobs(jobs, (it: IJob) => it.jobId)
            return {
                ...state,
                userJobs: {
                    error: undefined,
                    isFetching: false,
                    jobs: {
                        "Items": uniqueJobs
                    },
                    lastFetch: new Date()
                }
            };
        case getType(actions.pollUserJobs.failure):
            return {
                ...state,
                userJobs: {
                    ...state.userJobs,
                    error: action.payload,
                    isFetching: false,
                }
            };
        case getType(actions.orgJobs.request):
            return {
                ...state,
                orgJobs: {
                    ...state.orgJobs,
                    error: undefined,
                    isFetching: true
                }
            };
        case getType(actions.orgJobs.success):
            var jobs = state.orgJobs.jobs?.Items || []
            if (action.payload?.Items !== undefined) {
                jobs.push(...action.payload?.Items)
            }

            var uniqueJobs = getUniqueJobs(jobs, (it: IJob) => it.jobId)

            return {
                ...state,
                orgJobs: {
                    error: undefined,
                    isFetching: false,
                    jobs: {
                        "Items": uniqueJobs
                    },
                    lastFetch: new Date()
                }
            };
        case getType(actions.orgJobs.failure):
            return {
                ...state,
                orgJobs: {
                    ...state.orgJobs,
                    error: action.payload,
                    isFetching: false,
                }
            };
        case getType(actions.deleteJob.request):
            return {
                ...state,
                deleteJob: {
                    error: undefined,
                    isFetching: true,
                    success: false
                }
            };
        case getType(actions.deleteJob.success):
            return {
                ...state,
                deleteJob: {
                    error: undefined,
                    isFetching: false,
                    success: action.payload
                }
            };
        case getType(actions.deleteJob.failure):
            return {
                ...state,
                deleteJob: {
                    ...state.deleteJob,
                    error: action.payload,
                    isFetching: false,
                }
            };           
        case getType(actions.pollOrgJobs.request):
            return {
                ...state,
                orgJobs: {
                    ...state.orgJobs,
                    error: undefined,
                    isFetching: true
                }
            };
        case getType(actions.pollOrgJobs.success):
            var jobs = state.orgJobs.jobs?.Items || []
            if (action.payload?.Items !== undefined) {
                jobs.push(...action.payload?.Items)
            }

            var uniqueJobs = getUniqueJobs(jobs, (it: IJob) => it.jobId)
            return {
                ...state,
                orgJobs: {
                    error: undefined,
                    isFetching: false,
                    jobs: {
                        "Items": uniqueJobs
                    },
                    lastFetch: new Date()
                }
            };
        case getType(actions.pollOrgJobs.failure):
            return {
                ...state,
                orgJobs: {
                    ...state.orgJobs,
                    error: action.payload,
                    isFetching: false,
                }
            };
        case getType(actions.setUserJobsTimestampRange):
            return {
                ...state,
                userJobsRange: action.payload
            };
        case getType(actions.setOrgJobsTimestampRange):
            return {
                ...state,
                orgJobsRange: action.payload
            };
        case getType(actions.reset):
            return initialJobState;
        case getType(actions.clearJobInfo):
            return {
                ...state,
                jobStatus: {
                    ...state.jobStatus,
                    job: state.jobStatus.job ? {
                        ...state.jobStatus.job,
                        info: {}
                    } : undefined
                }
            };
        // Averaging feature selection mode actions
        case getType(actions.toggleSelectionMode):
            return {
                ...state,
                isSelectionMode: !state.isSelectionMode,
                selectedJobIds: !state.isSelectionMode ? state.selectedJobIds : []
            };
        case getType(actions.selectJob):
            if (state.selectedJobIds.includes(action.payload)) {
                return state;
            }
            return {
                ...state,
                selectedJobIds: [...state.selectedJobIds, action.payload]
            };
        case getType(actions.deselectJob):
            return {
                ...state,
                selectedJobIds: state.selectedJobIds.filter(id => id !== action.payload)
            };
        case getType(actions.clearSelection):
            return {
                ...state,
                selectedJobIds: []
            };
        case getType(actions.selectAllJobs):
            return {
                ...state,
                selectedJobIds: action.payload
            };
        default:
            return state;
    }
};
